import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";
import { Authorization } from "../../../Context/Authorization";

const selectStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", cursor: "pointer" };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none" };
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const UpdateEcMember = () => {
  const [vfcName, setVfcName] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [members, setMembers] = useState([]);
  const [fields, setFields] = useState([{ name: "", date: "", note: "" }]);
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");
  const [previousMembers, setPreviousMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  /* Auto-fetch VFCs when selectedJfmcType changes */
  useEffect(() => {
    if (!selectedJfmcType) return;
    const fetchVfc = async () => {
      setLoading(true);
      setSelectedVfc("");
      setMembers([]);
      setPreviousMembers([]);
      setFields([{ name: "", date: "", note: "" }]);
      setErrors({});
      setSubmitMessage("");
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/vfcs/names`,
          { jfmcTypeId: selectedJfmcType._id },
          config
        );
        setVfcName(response.data.vfcs || []);
      } catch (error) {
        console.error("Error fetching VFCs", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVfc();
  }, [selectedJfmcType]);

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setLoading(true);
    setSelectedVfc(vfcId);
    setErrors({});
    setSubmitMessage("");

    if (!vfcId) {
      setMembers([]);
      setPreviousMembers([]);
      setLoading(false);
      return;
    }

    try {
      const config = Authorization();

      const membersResponse = await fetch(`${Api}/api/members/getMembersByVFC/${vfcId}`, config);
      const membersData = await membersResponse.json();
      setMembers(membersData.members);

      const previousMembersResponse = await fetch(`${Api}/api/member/designation/previous/ECmembers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify({ vfcId }),
      });
      const previousMembersData = await previousMembersResponse.json();
      setPreviousMembers(previousMembersData);
    } catch (error) {
      console.error("Error fetching members or previous EC members", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    setFields([...fields, { name: "", date: "", note: "" }]);
  };

  const handleRemoveField = (index) => {
    if (fields.length === 1) return;
    const values = [...fields];
    values.splice(index, 1);
    setFields(values);
  };

  const handleChange = (index, event) => {
    const values = [...fields];
    values[index][event.target.name] = event.target.value;
    setFields(values);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedVfc) newErrors.vfc = "JFMC is required";
    fields.forEach((field, index) => {
      if (!field.name) newErrors[`name_${index}`] = "Member is required";
      if (!field.date) newErrors[`date_${index}`] = "Date is required";
      if (!field.note) newErrors[`note_${index}`] = "Note is required";
    });
    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      for (const field of fields) {
        const config = Authorization();
        const response = await fetch(`${Api}/api/member/designation`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...config.headers },
          body: JSON.stringify({
            id: field.name,
            name: members.find((member) => member._id === field.name)?.name,
            vfcId: selectedVfc,
            designation: "EC Member",
            date: field.date,
            note: field.note,
          }),
        });
        await logActivity(userId, "Updated New EC Member", "Update EC Member Page", Api);
        if (!response.ok) {
          const errorResponse = await response.json();
          console.error("Server Error:", errorResponse);
          throw new Error("Failed to update EC members");
        }
      }

      toast.success("Designation updated successfully");
      setSelectedVfc("");
      setMembers([]);
      setPreviousMembers([]);
      setFields([{ name: "", date: "", note: "" }]);
      setErrors({});
      setSubmitMessage("");
    } catch (error) {
      console.error("Error updating EC members", error);
      setSubmitMessage("Error updating EC members. Please try again.");
    }
  };

  /* ── No JFMC Type selected card ── */
  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

        {/* Left: Form card (2/3) */}
        <div style={{ flex: "0 0 66.666%", maxWidth: "66.666%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            {/* Card header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
                <i className="mdi mdi-account-group" />
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Update EC Members</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Assign EC members to a JFMC</div>
              </div>
            </div>

            <div style={{ padding: "24px 24px" }}>
              {submitMessage && (
                <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#C62828", marginBottom: 18 }}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }} />
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* JFMC Select */}
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>
                    <i className="mdi mdi-home-group" style={{ color: "#43A047", marginRight: 5 }} />
                    JFMC Name <span style={{ color: "#E53E3E" }}>*</span>
                  </label>
                  <select
                    value={selectedVfc}
                    onChange={handleVfcChange}
                    style={{ ...selectStyle, borderColor: errors.vfc ? "#FC8181" : "#E2E8F0" }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = errors.vfc ? "#FC8181" : "#E2E8F0")}
                  >
                    <option value="">— Select JFMC —</option>
                    {vfcName.map((vfc) => (
                      <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                    ))}
                  </select>
                  {errors.vfc && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors.vfc}</div>}
                </div>

                {/* Dynamic EC Member Rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
                  {fields.map((field, index) => (
                    <div
                      key={index}
                      style={{ background: "#F7FAF7", borderRadius: 10, border: "1px solid #E2E8F0", padding: "16px 16px 12px" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#1B5E20" }}>
                          <i className="mdi mdi-account" style={{ marginRight: 5 }} />
                          EC Member #{index + 1}
                        </span>
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveField(index)}
                            style={{ background: "#FFF5F5", border: "1px solid #FEB2B2", borderRadius: 6, color: "#C62828", fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          >
                            <i className="mdi mdi-close" /> Remove
                          </button>
                        )}
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        {/* Member */}
                        <div>
                          <label style={labelStyle}>
                            Member <span style={{ color: "#E53E3E" }}>*</span>
                          </label>
                          <select
                            name="name"
                            value={field.name}
                            onChange={(event) => handleChange(index, event)}
                            disabled={members.length === 0}
                            style={{ ...selectStyle, borderColor: errors[`name_${index}`] ? "#FC8181" : "#E2E8F0", background: members.length === 0 ? "#F7FAFC" : "#fff", color: members.length === 0 ? "#A0AEC0" : "#1A202C", cursor: members.length === 0 ? "not-allowed" : "pointer" }}
                            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                            onBlur={(e) => (e.target.style.borderColor = errors[`name_${index}`] ? "#FC8181" : "#E2E8F0")}
                          >
                            <option value="">— Select Member —</option>
                            {members.map((member) => (
                              <option key={member._id} value={member._id}>{member.name}</option>
                            ))}
                          </select>
                          {errors[`name_${index}`] && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors[`name_${index}`]}</div>}
                        </div>

                        {/* Date */}
                        <div>
                          <label style={labelStyle}>
                            Date <span style={{ color: "#E53E3E" }}>*</span>
                          </label>
                          <input
                            type="date"
                            name="date"
                            value={field.date}
                            onChange={(event) => handleChange(index, event)}
                            style={{ ...inputStyle, borderColor: errors[`date_${index}`] ? "#FC8181" : "#E2E8F0" }}
                            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                            onBlur={(e) => (e.target.style.borderColor = errors[`date_${index}`] ? "#FC8181" : "#E2E8F0")}
                          />
                          {errors[`date_${index}`] && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors[`date_${index}`]}</div>}
                        </div>

                        {/* Note */}
                        <div>
                          <label style={labelStyle}>
                            Note <span style={{ color: "#E53E3E" }}>*</span>
                          </label>
                          <input
                            type="text"
                            name="note"
                            placeholder="Enter note"
                            value={field.note}
                            onChange={(event) => handleChange(index, event)}
                            style={{ ...inputStyle, borderColor: errors[`note_${index}`] ? "#FC8181" : "#E2E8F0" }}
                            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                            onBlur={(e) => (e.target.style.borderColor = errors[`note_${index}`] ? "#FC8181" : "#E2E8F0")}
                          />
                          {errors[`note_${index}`] && <div style={{ fontSize: 11, color: "#C62828", marginTop: 4 }}>{errors[`note_${index}`]}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add another row */}
                <div style={{ marginBottom: 20 }}>
                  <button
                    type="button"
                    onClick={handleAddField}
                    style={{ background: "#E8F5E9", border: "1.5px dashed #43A047", borderRadius: 8, color: "#1B5E20", fontSize: 13, fontWeight: 600, padding: "8px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, width: "100%", justifyContent: "center" }}
                  >
                    <i className="mdi mdi-plus-circle-outline" style={{ fontSize: 16 }} />
                    Add another EC Member
                  </button>
                </div>

                {/* Submit */}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="submit"
                    style={{ padding: "9px 22px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px rgba(46,125,50,0.3)", display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <i className="mdi mdi-file-check" />
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Previous EC Members (1/3) */}
        <div style={{ flex: "0 0 33.333%", maxWidth: "33.333%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #EDF2F7", background: "#F7FAF7" }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-history" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Previous EC Members</span>
            </div>
            <div style={{ padding: "16px", maxHeight: 480, overflowY: "auto" }}>
              {previousMembers.length > 0 ? (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {previousMembers.map((member) => (
                    <li key={member._id} style={{ background: "#F7FAF7", borderRadius: 8, padding: "10px 12px", border: "1px solid #E2E8F0" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>{member.memberId?.name}</div>
                      <div style={{ fontSize: 11, color: "#718096", marginTop: 3, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ background: "#E8F5E9", color: "#1B5E20", padding: "1px 8px", borderRadius: "50rem", fontWeight: 600, fontSize: 11, border: "1px solid #A5D6A7" }}>{member.designation}</span>
                        <span>{new Date(member.date).toLocaleDateString("en-IN")}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#A0AEC0", fontSize: 13 }}>
                  <i className="mdi mdi-account-clock-outline" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  No previous EC members found
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UpdateEcMember;
